import { Button, CircularProgress } from '@mui/material';
import React, { Component } from 'react';
import { resendSignUpCode, signInWithRedirect, signUp } from '@aws-amplify/auth';
import CONFIG from '../../config';
import ConditionalSignupMessage from './Onboarding/ConditionalSignupMessage';
import Link from '@mui/material/Link';
import SignupOptions from './Onboarding/SignupOptions';
import SignupSuccess from './Onboarding/SignupSuccess';
import ThemeLogo from './ThemeLogo';
import { StyledSnackbar } from '../common/AppSnackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import W86AlphanumericTextfield from '../W86Components/W86AlphanumericTextField';
import { disposableEmail, whiteListEmail, nonWorkEmail } from '../../utils/EmailListUtil';
import { send_request, send_request_without_auth } from '../../utils/Request';
import { userStyles } from '../User/UserStyle';
import { v4 as uuidv4 } from 'uuid';
import { withStyles } from '@mui/styles';
const captchaKey = CONFIG.CAPTCHA;
const RECAPTCHA_SCRIPT_TIMEOUT_MS = 10000;

// Set this to false to disable new sign-ups.
// When VITE_ALLOWED_USERS is set (staging), signups are always disabled.
const ALLOW_NEW_SIGNUPS = import.meta.env.VITE_ALLOWED_USERS ? false : true;

// Hardcoded invite code for soft blocker
const INVITE_CODE = 'W86-2k25-xK9mP3nQ';

class CustomSignUp extends Component {
  state = {
    formState: '',
    loading: false,
    username: '',
    email: '',
    password: '',
    secret: '',
    user: {},
    isVerified: false,
    allowNewSignups: ALLOW_NEW_SIGNUPS,
    captchaLoadError: null,
    inviteCode: '',
  };

  componentDidMount = () => {
    this.setState({
      formState: this.props.formState,
      username: this.props.username,
    });

  };

  handleInputChange = event => {
    const { name, value, type, checked } = event.target;
    const check_type = ['radio', 'checkbox'].includes(type);
    this.setState(
      {
        [name]: check_type ? checked : value,
        checkedValue: check_type ? value : null,
      },
      () => {
        // If the email field is being changed, update the username
        if (name === 'email') {
          this.updateUsername(value);
        }
      }
    );
  };

  generateShortUUID = () => {
    return uuidv4().split('-')[0].substring(0, 6);
  };

  updateUsername = email => {
    if (email) {
      const baseUsername = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '_');
      const shortUUID = this.generateShortUUID();
      const username = `${baseUsername}_${shortUUID}`;
      this.setState({ username });
    }
  };

  isEmailDomainValid = async domain => {
    const isDisposable = disposableEmail().includes(domain);
    const isWhitelisted = whiteListEmail().includes(domain);
    const isNonWorkEmail = nonWorkEmail().includes(domain);

    if (isWhitelisted) {
      return {
        isValid: true,
        errorMessage: null,
      };
    }

    if (isDisposable) {
      return {
        isValid: false,
        errorMessage: 'Sorry, we do not allow sign ups with a disposable email',
      };
    }

    if (isNonWorkEmail) {
      return {
        isValid: false,
        errorMessage: 'Please create an account using a work email',
      };
    }

    try {
      const response = await send_request_without_auth(
        `authz-service/validate-email?domain=${domain}`,
        '',
        null,
        'GET'
      );
      const responseData = response?.data;

      if (!responseData?.isValid) {
        return {
          isValid: false,
          errorMessage:
            responseData?.domain +
            ' is not a valid email domain. Please create an account using a valid work email',
        };
      }

      return {
        isValid: true,
        errorMessage: null,
      };
    } catch (error) {
      console.error('Domain validation error:', error);
      return {
        isValid: false,
        errorMessage: 'Something went wrong while creating your account. Please try again later',
      };
    }
  };

  handleRegister = async e => {
    e.preventDefault();
    const { username, email, password, formState, secret, captchaLoadError } = this.state;
    if (captchaLoadError) {
      this.setState({
        openError: true,
        errorMessage: captchaLoadError,
        formState,
      });
      return;
    }
    if (this.state.isVerified) {
      if (email == '') {
        this.setState({
          openError: true,
          errorMessage: 'Email cannot be empty.',
          formState,
        });
      } else {
        this.setState({
          loading: true,
        });

        const emailDomain = email.split('@')[1];
        const validationResult = await this.isEmailDomainValid(emailDomain);

        if (validationResult.isValid) {
          await this.checkSecretKey();
        } else {
          this.setState({
            openError: true,
            loading: false,
            errorMessage: validationResult?.errorMessage,
            formState,
          });
        }
      }
    } else {
      this.setState({
        openError: true,
        errorMessage:
          'The reCAPTCHA was not completed. Please try again to confirm you are not a robot.',
        formState,
      });
    }
  };
  updateLastLogged = async () => {
    const json = await send_request(`authz-service/update-last-logged`, '', {});
  };

  checkSecretKey = async () => {
    const { username, email, password, formState, secret } = this.state;

    let json = { data: true, status: '200' };

    if (json && json.data == true && json.status == '200') {
      try {
        // Start the signup process
        const user = await signUp({
          username,
          password,
          options: {
            userAttributes: {
              email: email,
            },
          },
        });

        // Continue with the success flow
        setTimeout(() => {
          this.setState({
            formState: 'signUpSuccess',
            loading: false,
            confirmMessage: 'Further instructions have been sent to your email',
            openConfirm: true,
            email: email,
          });
        }, 2000);
      } catch (e) {
        console.log(e);
        if (e.message == 'User already exists') {
          this.setState({
            loading: false,
            openError: true,
            errorMessage: 'This username is not available. Please try another.',
            formState,
          });
        } else if (e.message.indexOf('This email is already being used by another account') != -1) {
          this.setState({
            loading: false,
            openError: true,
            errorMessage: 'This email is already being used by another account',
            formState,
          });
        } else {
          this.setState({
            loading: false,
            openError: true,
            errorMessage: e.message,
            formState,
          });
        }
      }
    } else {
      this.setState({
        openError: true,
        errorMessage: 'Invalid secret key',
        loading: false,
      });
    }
  };

  handleClose = () => {
    const { openError, openConfirm } = this.state;
    if (openError) {
      this.setState({ openError: false, verify: true });
    } else if (openConfirm) {
      this.setState({ openConfirm: false, verify: true });
    }
  };

  handleResend = () => {
    const { username } = this.state;
    this.setState({
      loading: true,
    });
    resendSignUpCode({ username: username })
      .then(user => {
        setTimeout(() => {
          this.setState({
            loading: false,
            confirmMessage: 'Instructions have been sent to your email.',
            openConfirm: true,
          });
        }, 2000);
      })
      .catch(e => {
        setTimeout(() => {
          this.setState({
            loading: false,
            openError: true,
            errorMessage: e.message,
          });
        }, 2000);
      });
  };
  componentDidUpdate(prevProps, prevState) {
    // Render reCAPTCHA Enterprise when the form becomes visible
    if (prevState.formState !== 'signUpWithEmail' && this.state.formState === 'signUpWithEmail') {
      this.renderRecaptcha();
    }
  }

  waitForRecaptchaApi = (isReady, timeoutMessage) => {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const poll = setInterval(() => {
        if (isReady()) {
          clearInterval(poll);
          resolve();
          return;
        }

        if (Date.now() - start >= RECAPTCHA_SCRIPT_TIMEOUT_MS) {
          clearInterval(poll);
          reject(new Error(timeoutMessage));
        }
      }, 100);
    });
  };

  ensureRecaptchaScript = (src, scriptMatch) => {
    const existingScript = document.querySelector(`script[src*="${scriptMatch}"]`);
    if (existingScript) return;

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  };

  loadRecaptchaScript = async () => {
    if (window.grecaptcha?.enterprise) return 'enterprise';
    if (window.grecaptcha?.render) return 'standard';

    try {
      this.ensureRecaptchaScript(
        'https://www.google.com/recaptcha/enterprise.js?render=explicit',
        'recaptcha/enterprise.js'
      );
      await this.waitForRecaptchaApi(
        () => Boolean(window.grecaptcha && window.grecaptcha.enterprise),
        'Timed out waiting for reCAPTCHA Enterprise'
      );
      return 'enterprise';
    } catch (enterpriseError) {
      console.warn('reCAPTCHA Enterprise failed, trying standard reCAPTCHA:', enterpriseError);
      this.ensureRecaptchaScript(
        'https://www.google.com/recaptcha/api.js?render=explicit',
        'recaptcha/api.js'
      );
      await this.waitForRecaptchaApi(
        () => Boolean(window.grecaptcha && typeof window.grecaptcha.render === 'function'),
        'Timed out waiting for standard reCAPTCHA'
      );
      return 'standard';
    }
  };

  renderRecaptcha = () => {
    this.setState({ captchaLoadError: null });
    const tryRender = async () => {
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        setTimeout(tryRender, 100);
        return;
      }

      try {
        const recaptchaMode = await this.loadRecaptchaScript();
        container.innerHTML = '';

        const renderFn =
          recaptchaMode === 'enterprise'
            ? window.grecaptcha.enterprise.render.bind(window.grecaptcha.enterprise)
            : window.grecaptcha.render.bind(window.grecaptcha);

        renderFn('recaptcha-container', {
          sitekey: captchaKey,
          callback: this.onCaptchaSuccess,
          'expired-callback': this.onCaptchaExpired,
          'error-callback': this.onCaptchaError,
        });
      } catch (error) {
        console.error('Failed to render reCAPTCHA:', error);
        this.setState({
          isVerified: false,
          captchaToken: null,
          captchaLoadError:
            'Could not load reCAPTCHA. Disable ad blockers/privacy extensions and refresh.',
        });
      }
    };

    tryRender();
  };

  onCaptchaSuccess = (token) => {
    this.setState({
      isVerified: true,
      captchaToken: token,
      captchaLoadError: null,
    });
  };

  onCaptchaExpired = () => {
    console.log('reCAPTCHA expired');
    this.setState({
      isVerified: false,
      captchaToken: null,
    });
  };

  onCaptchaError = () => {
    this.setState({
      isVerified: false,
      captchaToken: null,
      captchaLoadError: 'reCAPTCHA failed to load. Please refresh and try again.',
    });
  };

  signUpWithGG = () => {
    this.setState({ loading: true });
    signInWithRedirect({ provider: 'Google' }).catch(e => {
      console.log('Google sign in failed:', e);
      this.setState({ loading: false });
    });
  };

  render() {
    const { formState, openError, errorMessage, openConfirm, confirmMessage, allowNewSignups } =
      this.state;
    const { classes } = this.props;
    return (
      <div className="mainDiv">
        <StyledSnackbar
          open={openError}
          message={errorMessage}
          severity="error"
          onClose={this.handleClose}
          autoHideDuration={6000}
        />
        <StyledSnackbar
          open={openConfirm}
          message={confirmMessage}
          severity="success"
          onClose={this.handleClose}
          autoHideDuration={6000}
        />
        <Typography component="div">
          <div>
            <ThemeLogo />
          </div>
        </Typography>
        <ConditionalSignupMessage
          allowNewSignups={this.state.allowNewSignups}
          formState={this.state.formState}
          email={this.state.email}
        />
        {allowNewSignups && formState === 'signUp' && (
          <SignupOptions
            loading={this.state.loading}
            onSignUpWithEmail={() => this.setState({ formState: 'signUpWithEmail' })}
            onSignUpWithGoogle={this.signUpWithGG}
            onBackToSignIn={() => (window.location.href = '/')}
          />
        )}
        {allowNewSignups && formState === 'signUpWithEmail' && (
          <form onSubmit={this.handleRegister}>
            <div className="inputArea">
              <p className="inputDescription">Work Email</p>
              <TextField
                style={{ width: '100%' }}
                placeholder="Enter your work email"
                variant="outlined"
                id="email"
                key="email"
                name="email"
                type="email"
                value={this.state.email}
                onChange={this.handleInputChange}
              />
              <p className="inputDescription">Username</p>
              <W86AlphanumericTextfield
                customStyleProps={{}}
                handleChange={this.handleInputChange}
                value={this.state.username}
                props={{
                  id: 'username',
                  key: 'username',
                  name: 'username',
                  placeholder: 'Enter your username',
                }}
              />
              <p className="inputDescription">Password</p>
              <TextField
                style={{ width: '100%' }}
                placeholder="Enter your password"
                variant="outlined"
                id="password"
                key="password"
                name="password"
                type="password"
                onChange={this.handleInputChange}
              />
              <p className={'s-text noWrap'}>
                Must contain 6+ characters, special character(s), number(s), uppercase and lower
                case letter(s).
              </p>
              <p className="inputDescription">Invite Code</p>
              <TextField
                style={{ width: '100%' }}
                placeholder="Enter your invite code"
                variant="outlined"
                id="inviteCode"
                key="inviteCode"
                name="inviteCode"
                type="text"
                value={this.state.inviteCode}
                onChange={this.handleInputChange}
              />
            </div>
            <div className="actionArea">
              <Typography style={{ marginBottom: '20px' }}>
                <Link
                  href="https://www.workflow86.com/terms-and-conditions-of-use/"
                  target="_blank"
                  onClick={this.handleResetPassword}
                  underline="hover"
                >
                  By signing up for an account, you agree to our Terms of Service
                </Link>
              </Typography>
              <div id="recaptcha-container"></div>
              {this.state.captchaLoadError && (
                <Typography color="error" style={{ marginTop: '8px' }}>
                  {this.state.captchaLoadError}
                </Typography>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                }}
                className="flexStartMobile"
              >
                <Button
                  className="backButton"
                  variant="outlined"
                  color="info"
                  onClick={() => this.setState({ formState: 'signUp' })}
                >
                  Back
                </Button>
                <Button
                  className="signInButton"
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={this.handleRegister}
                  disabled={this.state.loading || this.state.inviteCode !== INVITE_CODE}
                  style={{ marginLeft: '16px' }}
                >
                  Register
                  {this.state.loading && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}
        {allowNewSignups && this.state.formState === 'signUpSuccess' && (
          <SignupSuccess
            email={this.state.email}
            loading={this.state.loading}
            handleResend={this.handleResend}
          />
        )}
      </div>
    );
  }
}

export default withStyles(userStyles)(CustomSignUp);
